import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { 
  ArrowLeft,
  Bell,
  Clock,
  DollarSign,
  Shield,
  User,
  Globe,
  Moon,
  Sun,
  Volume2,
  VolumeX,
  Smartphone,
  Mail,
  Calendar,
  FileText,
  CreditCard,
  HelpCircle,
  LogOut,
  Trash2
} from 'lucide-react';

const LawyerSettings = () => {
  const navigate = useNavigate();
  const { language, setLanguage, user, logout, toggleTheme, theme } = useApp();
  const [notifications, setNotifications] = useState({
    newBookings: true,
    messages: true,
    reminders: true,
    payments: true
  });

  const translations = {
    english: {
      settings: 'Settings',
      back: 'Back',
      profile: 'Profile Settings',
      consultationRates: 'Consultation Rates',
      availability: 'Availability',
      notifications: 'Notifications',
      privacy: 'Privacy & Security',
      appearance: 'Appearance',
      language: 'Language',
      payments: 'Payment Methods',
      help: 'Help & Support',
      logout: 'Logout',
      newBookings: 'New Bookings',
      messages: 'Messages',
      reminders: 'Appointment Reminders',
             accounting: 'Accounting',
       payout: 'Payout',
       invoices: 'Invoices',
       taxes: 'Taxes',
      videoCall: 'Video Call Rate',
      phoneCall: 'Phone Call Rate',
      inPerson: 'In-Person Rate',
      workingHours: 'Working Hours',
      setAvailability: 'Set Availability',
      totalActiveHours: 'Total Active Hours',
      totalOfflineHours: 'Total Offline Hours',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      english: 'English',
      hindi: 'Hindi',
                   addCard: 'Add Payment Method',
      contactSupport: 'Contact Support',
      about: 'About NyaySahaya',
      terms: 'Terms of Service',
      deleteAccount: 'Delete Account'
    },
    hindi: {
      settings: 'सेटिंग्स',
      back: 'वापस',
      profile: 'प्रोफाइल सेटिंग्स',
      consultationRates: 'परामर्श दरें',
      availability: 'उपलब्धता',
      notifications: 'सूचनाएं',
      privacy: 'गोपनीयता और सुरक्षा',
      appearance: 'दिखावट',
      language: 'भाषा',
      payments: 'भुगतान विधियां',
      help: 'सहायता और समर्थन',
      logout: 'लॉगआउट',
      newBookings: 'नई बुकिंग',
      messages: 'संदेश',
      reminders: 'नियुक्ति अनुस्मारक',
             accounting: 'लेखांकन',
       payout: 'भुगतान',
       invoices: 'चालान',
       taxes: 'कर',
      videoCall: 'वीडियो कॉल दर',
      phoneCall: 'फोन कॉल दर',
      inPerson: 'व्यक्तिगत दर',
      workingHours: 'कार्य समय',
      setAvailability: 'उपलब्धता सेट करें',
      totalActiveHours: 'कुल सक्रिय घंटे',
      totalOfflineHours: 'कुल ऑफलाइन घंटे',
      darkMode: 'डार्क मोड',
      lightMode: 'लाइट मोड',
      english: 'अंग्रेजी',
      hindi: 'हिंदी',
             addCard: 'भुगतान विधि जोड़ें',
       contactSupport: 'समर्थन से संपर्क करें',
             about: 'NyaySahaya के बारे में',
      terms: 'सेवा की शर्तें',
      deleteAccount: 'खाता हटाएं'
    }
  };

  const t = translations[language] || translations.english;

  const handleLogout = () => {
    // Clear user data and redirect to auth page
    logout();
    // Note: logout() function in AppContext already handles navigation to /auth
  };

  const handleDeleteAccount = () => {
    // Show confirmation dialog
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      // Clear all user data
      localStorage.removeItem('user');
      localStorage.removeItem('lawyer_profile');
      localStorage.removeItem('lawyer_availability');
      localStorage.removeItem('lawyer_status');
      localStorage.removeItem('lawyer_scheduled_offline');
      localStorage.removeItem('language');
      localStorage.removeItem('theme');
      
      // Logout and redirect to auth page
      logout();
      // Note: logout() function in AppContext already handles navigation to /auth
    }
  };

  const settingsSections = [
    {
      title: t.profile,
      icon: User,
      items: [
        { label: 'Edit Profile', action: () => navigate('/lawyer-profile-settings') },
        { label: 'Professional Details', action: () => navigate('/lawyer-profile-settings', { state: { activeTab: 'professional' } }) },
        { label: 'Consultation Rates', action: () => navigate('/lawyer-profile-settings', { state: { activeTab: 'consultation' } }) },
        { label: 'Accounts & Documents', action: () => navigate('/lawyer-profile-settings', { state: { activeTab: 'accounts' } }) }
      ]
    },
    
    {
      title: t.availability,
      icon: Clock,
      items: [
        { label: t.workingHours, action: () => navigate('/lawyer-working-hours') },
        { label: t.setAvailability, action: () => navigate('/lawyer-set-availability') }
      ]
    },
         {
       title: t.notifications,
       icon: Bell,
       items: [
         { 
           label: t.newBookings, 
           action: () => setNotifications(prev => ({ ...prev, newBookings: !prev.newBookings })),
           toggle: notifications.newBookings
         },
         { 
           label: t.messages, 
           action: () => setNotifications(prev => ({ ...prev, messages: !prev.messages })),
           toggle: notifications.messages
         },
         { 
           label: t.reminders, 
           action: () => setNotifications(prev => ({ ...prev, reminders: !prev.reminders })),
           toggle: notifications.reminders
         }
       ]
     },
     {
       title: t.accounting,
       icon: DollarSign,
       items: [
         { 
           label: t.payout, 
           action: () => console.log('Payout'),
           icon: DollarSign
         },
         { 
           label: t.invoices, 
           action: () => console.log('Invoices'),
           icon: FileText
         },
         { 
           label: t.taxes, 
           action: () => console.log('Taxes'),
           icon: Shield
         }
       ]
     },
    {
      title: t.appearance,
      icon: theme === 'dark' ? Moon : Sun,
      items: [
        { 
          label: theme === 'dark' ? t.lightMode : t.darkMode, 
          action: toggleTheme,
          icon: theme === 'dark' ? Sun : Moon
        },
                 { 
           label: t.language, 
           action: () => {
             const newLanguage = language === 'english' ? 'hindi' : 'english';
             setLanguage(newLanguage);
           },
           icon: Globe
         }
      ]
    },
    
    {
      title: t.help,
      icon: HelpCircle,
      items: [
        { label: t.contactSupport, action: () => console.log('Contact support') },
        { label: t.about, action: () => console.log('About') },
        { label: t.terms, action: () => console.log('Terms') },
        { label: t.privacy, action: () => console.log('Privacy') }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-lavender-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/lawyer-dashboard')}
                className="w-10 h-10 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-purple-500" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t.settings}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage your lawyer account
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Settings Sections */}
        <div className="space-y-6">
          {settingsSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                    <section.icon className="w-4 h-4 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {section.title}
                  </h3>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {section.items.map((item, itemIndex) => (
                    <motion.button
                      key={item.label}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={item.action}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                    >
                      <div className="flex items-center gap-3">
                        {item.icon && (
                          <item.icon className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="text-gray-900 dark:text-white">
                          {item.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.toggle !== undefined && (
                          <div className={`w-6 h-3 rounded-full transition-colors duration-200 ${
                            item.toggle ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
                          }`}>
                            <div className={`w-3 h-3 bg-white rounded-full transition-transform duration-200 ${
                              item.toggle ? 'translate-x-3' : 'translate-x-0'
                            }`} />
                          </div>
                        )}
                        <div className="w-2 h-2 border-r-2 border-t-2 border-gray-400 transform rotate-45" />
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}

                     {/* Logout Button */}
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6, delay: 0.7 }}
           >
             <button
               onClick={handleLogout}
               className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center gap-3"
             >
               <LogOut className="w-5 h-5" />
               {t.logout}
             </button>
           </motion.div>

           {/* Delete Account Button */}
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6, delay: 0.8 }}
           >
             <button
               onClick={handleDeleteAccount}
               className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center gap-3"
             >
               <Trash2 className="w-5 h-5" />
               {t.deleteAccount}
             </button>
           </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LawyerSettings;
